import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { W02Tauler } from '@/pages/operacio/W02Tauler';
import { W03Activacions } from '@/pages/operacio/W03Activacions';
import { W05NovaActivacio } from '@/pages/operacio/W05NovaActivacio';
import { Ajustos } from '@/pages/administracio/Ajustos';
import { AjustosMarc } from '@/pages/administracio/marc/AjustosMarc';
import { W20Usuaris } from '@/pages/administracio/W20Usuaris';
import { W21DetallUsuari } from '@/pages/administracio/W21DetallUsuari';
import { W22Rols } from '@/pages/administracio/W22Rols';
import { W23UsuarisInformar } from '@/pages/administracio/W23UsuarisInformar';
import { W24Arees } from '@/pages/administracio/W24Arees';
import { W25Circuits } from '@/pages/administracio/W25Circuits';
import { W26Canals } from '@/pages/administracio/W26Canals';
import { W27Plantilles } from '@/pages/administracio/W27Plantilles';
import { W28Zones } from '@/pages/administracio/W28Zones';
import { W29Integracions } from '@/pages/administracio/W29Integracions';
import { W30Versions } from '@/pages/administracio/W30Versions';
import { W31Configuracio } from '@/pages/administracio/W31Configuracio';
import { W32Documentacio } from '@/pages/administracio/W32Documentacio';
import { W33DetallDocument } from '@/pages/administracio/W33DetallDocument';
import { W35PoliticaFitxers } from '@/pages/administracio/W35PoliticaFitxers';
import { W37Importacio } from '@/pages/administracio/W37Importacio';
import { W40Indicadors } from '@/pages/informes/W40Indicadors';
import { W41Informes } from '@/pages/informes/W41Informes';
import { W42Generador } from '@/pages/informes/W42Generador';
import { W43InformeActivacio } from '@/pages/informes/W43InformeActivacio';
import { W44Auditoria } from '@/pages/informes/W44Auditoria';
import { W45Tracabilitat } from '@/pages/informes/W45Tracabilitat';
import { W46Monitoratge } from '@/pages/informes/W46Monitoratge';
import { W47Recurrents } from '@/pages/informes/W47Recurrents';
import { useAuth } from '@/auth/AuthContext';
import { AppLayout } from '@/layout/AppLayout';
import {
  ELEMENTS_NAV,
  REDIRECCIONS,
  RUTES_OCULTES,
  SECCIONS,
  rutaSeccio,
  type SeccioNav,
} from '@/navigation/navigation';
import { W10Calendaris } from '@/pages/planificacio/W10Calendaris';
import { W11EditorCalendari } from '@/pages/planificacio/W11EditorCalendari';
import { W13Rotacio } from '@/pages/planificacio/W13Rotacio';
import { W14Sollicituds } from '@/pages/planificacio/W14Sollicituds';
import { W15Cobertura } from '@/pages/planificacio/W15Cobertura';
import { W16Experts } from '@/pages/planificacio/W16Experts';
import { W17Historial } from '@/pages/planificacio/W17Historial';
import { Carregant, NoTrobat, SensePermis } from '@/pages/EstatsPagina';
import { PantallaPendent } from '@/pages/PantallaPendent';
import { W01Login } from '@/pages/W01Login';
import { useMe } from '@/permisos/MeContext';
import { compleix } from '@/permisos/permisos';
import { RequirePermis } from '@/permisos/RequirePermis';

const PANTALLES: Partial<Record<string, ReactNode>> = {
  'W-02': <W02Tauler />,
  'W-03': <W03Activacions />,
  // Llista i detall comparteixen pantalla (mestre-detall): el component es conserva en navegar.
  'W-04': <W03Activacions />,
  'W-05': <W05NovaActivacio />,
  'W-10': <W10Calendaris />,
  'W-11': <W11EditorCalendari />,
  'W-13': <W13Rotacio />,
  'W-14': <W14Sollicituds />,
  'W-15': <W15Cobertura />,
  'W-16': <W16Experts />,
  'W-17': <W17Historial />,
  'AJ-00': <Ajustos />,
  'W-20': <W20Usuaris />,
  'W-21': <W21DetallUsuari />,
  'W-22': <W22Rols />,
  'W-23': <W23UsuarisInformar />,
  'W-24': <W24Arees />,
  'W-25': <W25Circuits />,
  'W-26': <W26Canals />,
  'W-27': <W27Plantilles />,
  'W-28': <W28Zones />,
  'W-29': <W29Integracions />,
  'W-30': <W30Versions />,
  'W-31': <W31Configuracio />,
  'W-32': <W32Documentacio />,
  'W-33': <W33DetallDocument />,
  'W-35': <W35PoliticaFitxers />,
  'W-37': <W37Importacio />,
  'W-40': <W40Indicadors />,
  'W-41': <W41Informes />,
  'W-42': <W42Generador />,
  'W-43': <W43InformeActivacio />,
  'W-44': <W44Auditoria />,
  'W-45': <W45Tracabilitat />,
  'W-46': <W46Monitoratge />,
  'W-47': <W47Recurrents />,
};

function Inici() {
  const { me } = useMe();
  const primer = ELEMENTS_NAV.find((e) => compleix(me, e.permis));
  return primer ? <Navigate to={primer.ruta} replace /> : <NoTrobat />;
}

/** Arrel d'una secció sense pantalla pròpia (/planificacio, /analisi): primera pestanya permesa. */
function EntradaSeccio({ seccio }: { seccio: SeccioNav }) {
  const { me } = useMe();
  const s = SECCIONS.find((x) => x.clau === seccio);
  const ruta = s ? rutaSeccio(me, s) : null;
  return ruta ? <Navigate to={ruta} replace /> : <SensePermis />;
}

export function AppRoutes() {
  const { autenticat, carregant } = useAuth();
  if (!autenticat) return carregant ? <Carregant /> : <W01Login />;
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Inici />} />
        {[...ELEMENTS_NAV, ...RUTES_OCULTES].map((e) => (
          <Route
            key={e.clau}
            path={e.ruta}
            element={
              e.seccio === 'ajustos' ? (
                <AjustosMarc>
                  <RequirePermis permis={e.permis}>
                    {PANTALLES[e.pantalla] ?? <PantallaPendent element={e} />}
                  </RequirePermis>
                </AjustosMarc>
              ) : (
                <RequirePermis permis={e.permis}>
                  {PANTALLES[e.pantalla] ?? <PantallaPendent element={e} />}
                </RequirePermis>
              )
            }
          />
        ))}
        {REDIRECCIONS.map((r) => (
          <Route key={r.de} path={r.de} element={<Navigate to={r.a} replace />} />
        ))}
        <Route path="/planificacio" element={<EntradaSeccio seccio="guardies" />} />
        <Route path="/analisi" element={<EntradaSeccio seccio="analisi" />} />
        <Route path="*" element={<NoTrobat />} />
      </Route>
    </Routes>
  );
}
